// hooks/useAutoSave.js
// Auto-save and draft persistence for multi-product creator
// Uses localStorage for draft storage

import { useState, useCallback, useEffect, useRef } from "react";

const STORAGE_KEY = "multiProductCreator_drafts";
const AUTO_SAVE_INTERVAL = 30000; // 30 seconds
const MAX_DRAFTS = 10;

export const useAutoSave = (tabs, enabled = true) => {
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [lastSaved, setLastSaved] = useState(null);
  const saveTimeoutRef = useRef(null);

  // Load drafts from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setSavedDrafts(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error("Failed to load drafts:", error);
      setSavedDrafts([]);
    }
  }, []);

  // Save current tabs to localStorage
  const saveDraft = useCallback(
    (name = null) => {
      if (!tabs || tabs.length === 0) return null;

      const draft = {
        id: `draft-${Date.now()}`,
        name: name || `Draft ${new Date().toLocaleString()}`,
        tabs: tabs.map((tab) => ({
          id: tab.id,
          data: tab.data,
          status: tab.status === "saved" ? "saved" : "draft",
        })),
        createdAt: Date.now(),
        productCount: tabs.length,
      };

      setSavedDrafts((prev) => {
        // Keep only the most recent drafts
        const updated = [draft, ...prev].slice(0, MAX_DRAFTS);

        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch (error) {
          console.error("Failed to save draft:", error);
        }

        return updated;
      });

      setLastSaved(Date.now());
      return draft;
    },
    [tabs]
  );

  // Load a saved draft
  const loadDraft = useCallback(
    (draftId) => {
      const draft = savedDrafts.find((d) => d.id === draftId);
      if (!draft) return null;

      return draft.tabs;
    },
    [savedDrafts]
  );

  // Delete a draft
  const deleteDraft = useCallback((draftId) => {
    setSavedDrafts((prev) => {
      const updated = prev.filter((d) => d.id !== draftId);

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (error) {
        console.error("Failed to delete draft:", error);
      }

      return updated;
    });
  }, []);

  // Clear all drafts
  const clearDrafts = useCallback(() => {
    setSavedDrafts([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear drafts:", error);
    }
  }, []);

  // Auto-save effect
  useEffect(() => {
    if (!enabled) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set up new auto-save
    saveTimeoutRef.current = setTimeout(() => {
      // Only auto-save if there are unsaved changes
      const hasUnsavedChanges = tabs.some(
        (tab) =>
          tab.status === "draft" &&
          tab.data.name &&
          (parseFloat(tab.data.grossWeight) > 0 || tab.data.type)
      );

      if (hasUnsavedChanges) {
        saveDraft("Auto-save");
      }
    }, AUTO_SAVE_INTERVAL);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [tabs, enabled, saveDraft]);

  // Save on page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasUnsavedChanges = tabs.some(
        (tab) =>
          tab.status === "draft" &&
          tab.data.name &&
          (parseFloat(tab.data.grossWeight) > 0 || tab.data.type)
      );

      if (hasUnsavedChanges) {
        saveDraft("Auto-save (before unload)");
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [tabs, saveDraft]);

  return {
    savedDrafts,
    lastSaved,
    saveDraft,
    loadDraft,
    deleteDraft,
    clearDrafts,
  };
};

export default useAutoSave;
